/**
 * My hours — plan §6.6, `employees-my-hours.png`.
 *
 * A phone screen, and the only one a crew member opens on their own device
 * rather than the truck's iPad. So it is one narrow column, the week total is
 * the biggest thing on it, and every day says how it was made: in, out, the
 * break that was deducted, how many stops.
 *
 * A corrected punch says so, with who fixed it. Hiding that would make the
 * number arrive without an explanation, which is exactly the thing people
 * distrust about a timesheet.
 */
import { useCallback, useEffect, useState } from 'react'
import type { MeResponse } from '@rowted/shared'
import { Avatar, Button, Card, EmptyState } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import {
  clockTime, hoursLabel, shiftWeeks, shortDuration, weekLabel, weekdayLabel,
  type HoursDay, type HoursPayload,
} from '../lib/hours.js'

type Period = 'this' | 'last'

export function MyHours({ me }: { me: MeResponse }) {
  const [period, setPeriod] = useState<Period>('this')
  const [data, setData] = useState<HoursPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      // "Last week" is seven days back from any date in this week — the server
      // still finds the Monday, so the client never has to know which day the
      // week starts on.
      const week = period === 'last' ? shiftWeeks(new Date(), -1) : undefined
      setData(await request<HoursPayload>(
        `/crew-app/my-hours${week ? `?week=${week}` : ''}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Your hours could not load.')
    }
  }, [period])

  useEffect(() => { void load() }, [load])

  const firstName = (data?.user?.name ?? me.user.name).split(' ')[0]

  return (
    <div className="flex min-h-app flex-col bg-bg">
      <header className="shrink-0 bg-surface px-5 pb-4 pt-5">
        <div className="mx-auto flex max-w-[560px] items-center gap-3">
          <span className="text-card font-semibold text-text">{me.tenant.name}</span>
          <Avatar name={me.user.name} size={34} self className="ml-auto" />
        </div>

        <div className="mx-auto mt-3 max-w-[560px]">
          <p className="m-0 text-body text-text-muted">
            Hi {firstName}
            {data?.crew && <> · {data.crew}</>}
          </p>
          <h1 className="m-0 text-[28px] font-bold leading-tight text-text">My hours</h1>
        </div>

        <div className="mx-auto mt-4 grid max-w-[560px] grid-cols-3 gap-2.5">
          <PeriodButton
            label="This week" active={period === 'this'} onClick={() => setPeriod('this')} />
          <PeriodButton
            label="Last week" active={period === 'last'} onClick={() => setPeriod('last')} />
          <PeriodButton
            label="Pay stubs" active={false}
            onClick={() => setNotice('Pay stubs arrive with payroll export in Phase 8.')} />
        </div>
      </header>

      <main className="min-h-0 flex-1 border-t border-line px-5 py-4">
        <div className="mx-auto flex max-w-[560px] flex-col gap-3">
          {error && (
            <EmptyState title="Your hours could not load" body={error}
              action={{ label: 'Try again', onClick: () => void load() }} />
          )}

          {data && (
            <>
              <SummaryCard data={data} />

              {data.days.length === 0
                ? (
                  <Card className="p-5">
                    <p className="m-0 text-body text-text-muted">
                      No punches this week yet. Hours show up here as soon as you clock
                      in on the truck’s iPad.
                    </p>
                  </Card>
                )
                : (
                  <Card className="px-4 py-1">
                    {data.days.map((day, index) => (
                      <DayRowView key={day.date} day={day} first={index === 0} />
                    ))}
                  </Card>
                )}

              <Card className="flex flex-wrap items-start gap-4 p-4">
                <div className="min-w-[180px] flex-1">
                  <p className="m-0 text-card font-bold text-text">Something wrong?</p>
                  <p className="m-0 mt-1 text-body text-text-muted">
                    Ask {data.leadName ?? 'your lead'} to fix a punch. You’ll get a text
                    when it’s done.
                  </p>
                </div>
                <Button
                  onClick={() => setNotice(data.leadName
                    ? `${data.leadName} fixes punches on the iPad. Correction requests send a text in Phase 7.`
                    : 'Your crew has no lead set yet — the office can fix a punch.')}
                >
                  Request a fix
                </Button>
              </Card>
            </>
          )}
        </div>
      </main>

      <footer className="sticky bottom-0 shrink-0 border-t border-line bg-surface px-5 py-3">
        <div className="mx-auto grid max-w-[560px] grid-cols-2 gap-3">
          <Button size="big" onClick={() => setNotice('Tomorrow’s route opens on the truck’s iPad.')}>
            Tomorrow’s route
          </Button>
          <Button size="big" onClick={() => setNotice('Time-off requests arrive in Phase 8.')}>
            Request time off
          </Button>
        </div>
      </footer>

      {notice && <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>}
    </div>
  )
}

function PeriodButton({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-h-[52px] rounded-card border px-2 text-body font-bold transition-colors',
        active
          ? 'border-accent bg-accent-soft text-accent'
          : 'border-line bg-surface text-text hover:bg-hover')}
    >
      {label}
    </button>
  )
}

function SummaryCard({ data }: { data: HoursPayload }) {
  const overtime = data.totalMinutes > data.overtimeAfterHours * 60

  return (
    <Card className="flex flex-wrap items-start justify-between gap-4 p-5">
      <div>
        <p className="m-0 text-body text-text-muted">{weekLabel(data.week)}</p>
        <p className="m-0 text-[40px] font-bold leading-none text-text">
          {hoursLabel(data.totalMinutes)}
        </p>
        <p className="m-0 mt-1.5 text-body text-text-muted">
          of {data.standardHours}
          {data.daysLeft > 0 && (
            <> · {data.daysLeft} {data.daysLeft === 1 ? 'day' : 'days'} left</>
          )}
        </p>
      </div>

      <div className="text-right">
        {data.openSince
          ? (
            <p className="m-0 text-card font-bold text-accent">
              ● Clocked in · {clockTime(data.openSince)}
              {data.openAt && (
                <span className="block font-normal text-text-muted">{data.openAt}</span>
              )}
            </p>
          )
          : <p className="m-0 text-card font-bold text-text-muted">Not clocked in</p>}
        <p className={cn('m-0 mt-1 text-body', overtime ? 'text-warn' : 'text-text-muted')}>
          {overtime
            ? `Over ${data.overtimeAfterHours} h — overtime`
            : `Overtime after ${data.overtimeAfterHours} h`}
        </p>
      </div>
    </Card>
  )
}

function DayRowView({ day, first }: { day: HoursDay; first: boolean }) {
  const detail = [
    day.breakMinutes > 0 ? `Lunch ${day.breakMinutes} min` : null,
    day.stops > 0 ? `${day.stops} ${day.stops === 1 ? 'stop' : 'stops'}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className={cn('flex items-center gap-4 py-3.5', !first && 'border-t border-line')}>
      <span className="w-[42px] shrink-0 text-body font-bold text-text">
        {weekdayLabel(day.date)}
      </span>

      <div className="min-w-0 flex-1">
        <p className="m-0 text-body text-text">
          {clockTime(day.firstIn)} – {day.open ? 'now' : clockTime(day.lastOut)}
        </p>
        {day.correction
          ? (
            <p className="m-0 text-body text-danger">
              {day.correction.reason ?? 'Punch corrected'}
              {day.correction.byName && <> — fixed by {day.correction.byName}</>}
            </p>
          )
          : (
            <p className="m-0 text-body text-text-muted">
              {day.open ? `${detail ? `${detail} · ` : ''}in progress` : detail || 'No stops'}
            </p>
          )}
      </div>

      <span className="shrink-0 text-card font-bold text-text">
        {day.open ? shortDuration(day.minutes) : hoursLabel(day.minutes)}
      </span>
    </div>
  )
}

